/**
 * Amplify API Wrapper with Auth Error Handling for Monutrack
 *
 * This module wraps AWS Amplify's API methods (get, post, put, del, patch) with:
 * 1. Automatic tenant domain injection (when available)
 * 2. Authentication error detection and handling
 * 3. Support for multi-tenant architecture
 *
 * Usage: Replace imports from 'aws-amplify/api' with this wrapper
 *
 * Before:
 *   import { get, post, put, del } from 'aws-amplify/api'
 *
 * After:
 *   import { get, post, put, del } from '@/api/lib/amplify-wrapper'
 *
 * The wrapper will automatically:
 * - Inject X-Tenant-Domain header when tenant context is available
 * - Detect NotAuthorizedException errors
 * - Sign out the user
 * - Redirect to login page
 * - Preserve return URL for post-login redirect
 */

import {
  get as amplifyGet,
  post as amplifyPost,
  put as amplifyPut,
  patch as amplifyPatch,
  del as amplifyDel,
} from 'aws-amplify/api'
import { isAuthError, handleAuthError } from './error-handler'

// JWT token storage for signing sessions using localStorage
// This is separate from Amplify auth and only used for OTP-verified signing sessions
// Stored in localStorage so it persists across page refreshes in the same browser

const JWT_STORAGE_KEY = 'monumenta_session_jwt'

export function setGlobalJWT(token: string | null) {
  console.log('🔑 [API Client] JWT updated:', token ? 'SET' : 'CLEARED')

  if (token) {
    // Store JWT in localStorage
    try {
      localStorage.setItem(JWT_STORAGE_KEY, token)
      console.log('✅ [API Client] JWT stored in localStorage')
    } catch (error) {
      console.error('❌ [API Client] Failed to store JWT in localStorage:', error)
      // Fall back to memory storage if localStorage fails
    }
  } else {
    // Clear JWT from localStorage
    try {
      localStorage.removeItem(JWT_STORAGE_KEY)
      console.log('✅ [API Client] JWT removed from localStorage')
    } catch (error) {
      console.error('❌ [API Client] Failed to remove JWT from localStorage:', error)
    }
  }
}

export function getGlobalJWT(): string | null {
  try {
    const token = localStorage.getItem(JWT_STORAGE_KEY)
    if (!token) {
      return null
    }

    // Validate JWT hasn't expired
    try {
      // Decode JWT payload (without verifying signature - server will verify)
      const parts = token.split('.')
      if (parts.length !== 3) {
        console.warn('⚠️ [API Client] Invalid JWT format, clearing')
        clearGlobalJWT()
        return null
      }

      const payload = JSON.parse(atob(parts[1]))
      const exp = payload.exp

      if (exp) {
        const now = Math.floor(Date.now() / 1000)
        if (exp < now) {
          console.warn('⚠️ [API Client] JWT expired, clearing')
          clearGlobalJWT()
          return null
        }
      }

      console.log('🔑 [API Client] Valid JWT retrieved from localStorage')
      return token
    } catch (decodeError) {
      console.error('❌ [API Client] Failed to decode JWT, clearing:', decodeError)
      clearGlobalJWT()
      return null
    }
  } catch (error) {
    console.error('❌ [API Client] Failed to retrieve JWT from localStorage:', error)
    return null
  }
}

export function clearGlobalJWT() {
  console.log('🔑 [API Client] Clearing JWT')
  setGlobalJWT(null)
}

/**
 * Get tenant domain for API calls
 * If VITE_TENANT_DOMAIN env var is set, always use it.
 * Otherwise, use the actual hostname.
 */
function getTenantDomain(): string | undefined {
  // If env var is set, always use it
  const envTenantDomain = import.meta.env.VITE_TENANT_DOMAIN
  if (envTenantDomain) {
    return envTenantDomain
  }

  // Otherwise use the actual hostname
  if (typeof window !== 'undefined') {
    return window.location.hostname
  }

  return undefined
}

/**
 * Inject tenant domain header into API request input
 *
 * Automatically reads tenantDomain from URL/context and injects it
 * as X-Tenant-Domain header when available.
 *
 * @param input - Amplify API input (get/post/put/patch/del)
 * @returns Input with X-Tenant-Domain header injected (if tenant domain is available)
 */
function injectTenantHeader<T extends Parameters<typeof amplifyGet>[0] | Parameters<typeof amplifyPost>[0] | Parameters<typeof amplifyPut>[0] | Parameters<typeof amplifyPatch>[0] | Parameters<typeof amplifyDel>[0]>(
  input: T
): T {
  const tenantDomain = getTenantDomain()

  const headers: Record<string, string> = {
    ...input.options?.headers,
  }

  // Only inject tenant header if we have a tenant domain
  if (tenantDomain) {
    headers['X-Tenant-Domain'] = tenantDomain
    console.log('🔧 [API Client] Injecting tenant domain header:', tenantDomain)
  } else {
    console.log('🔧 [API Client] No tenant domain detected, skipping tenant header injection')
  }

  // Check if authMode is set in options (type-unsafe access for extensibility)
  const existingAuthMode = (input.options as any)?.authMode
  console.log('🔧 [API Client] Final headers:', headers)
  console.log('🔧 [API Client] Auth mode:', existingAuthMode || 'default (userPool)')

  const options: any = {
    ...input.options,
    headers,
    // Ensure authMode is preserved - defaults to 'userPool' for IAM v4 signatures
    authMode: existingAuthMode || 'userPool',
  }

  return {
    ...input,
    options,
  } as T
}

/**
 * Wrap an Amplify API call with auth error handling and tenant injection
 * Returns the Operation directly but with error handling applied
 */
function wrapApiCall<T>(
  apiCall: () => T
): T {
  const operation = apiCall()

  // Create a proxy to intercept the response access
  const wrappedOperation = {
    ...operation,
    response: (async () => {
      try {
        return await (operation as any).response
      } catch (error) {
        // 🔍 DEBUG: Log the raw Amplify error to understand its structure
        console.error('🔍 [Amplify Wrapper] Raw error caught:', {
          errorType: error?.constructor?.name,
          errorMessage: (error as any)?.message,
          errorResponse: (error as any)?.response,
          errorStatus: (error as any)?.response?.statusCode || (error as any)?.status || (error as any)?.statusCode,
          fullError: error,
        })

        // 🚫 Check for 403 errors and enhance them to prevent retries
        const status = (error as any)?.response?.statusCode || (error as any)?.status || (error as any)?.statusCode
        if (status === 403) {
          console.warn('🚫 [Amplify Wrapper] 403 Forbidden detected - enhancing error to prevent retries')
          const enhancedError: any = new Error('User lacks permission to access this resource')
          enhancedError.status = 403
          enhancedError.statusCode = 403
          enhancedError.response = { status: 403, statusCode: 403 }
          enhancedError.originalError = error
          throw enhancedError
        }

        // Check if this is an auth error
        if (isAuthError(error)) {
          const domain = getTenantDomain()
          await handleAuthError(error, domain)
          throw error // Won't execute due to redirect
        }

        // Re-throw non-auth errors
        throw error
      }
    })(),
  }

  return wrappedOperation as T
}

/**
 * GET request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify GET input (X-Tenant-Domain will be auto-injected if available)
 *
 * @example
 * ```typescript
 * // Before: Had to pass tenantDomain manually (if needed)
 * const { response } = await get({
 *   apiName: 'monumenta',
 *   path: '/employees',
 *   options: { headers: { 'X-Tenant-Domain': tenantDomain } }
 * })
 *
 * // After: Tenant domain auto-injected when available!
 * const { response } = await get({
 *   apiName: 'monumenta',
 *   path: '/employees'
 * })
 * ```
 */
export function get(input: Parameters<typeof amplifyGet>[0]): ReturnType<typeof amplifyGet> {
  const inputWithTenant = injectTenantHeader(input)
  return wrapApiCall(() => amplifyGet(inputWithTenant))
}

/**
 * POST request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify POST input (X-Tenant-Domain will be auto-injected if available)
 */
export function post(input: Parameters<typeof amplifyPost>[0]): ReturnType<typeof amplifyPost> {
  const inputWithTenant = injectTenantHeader(input)
  return wrapApiCall(() => amplifyPost(inputWithTenant))
}

/**
 * PUT request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify PUT input (X-Tenant-Domain will be auto-injected if available)
 */
export function put(input: Parameters<typeof amplifyPut>[0]): ReturnType<typeof amplifyPut> {
  const inputWithTenant = injectTenantHeader(input)
  return wrapApiCall(() => amplifyPut(inputWithTenant))
}

/**
 * PATCH request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify PATCH input (X-Tenant-Domain will be auto-injected if available)
 */
export function patch(input: Parameters<typeof amplifyPatch>[0]): ReturnType<typeof amplifyPatch> {
  const inputWithTenant = injectTenantHeader(input)
  return wrapApiCall(() => amplifyPatch(inputWithTenant))
}

/**
 * DELETE request with automatic tenant injection and auth error handling
 *
 * @param input - Amplify DELETE input (X-Tenant-Domain will be auto-injected if available)
 */
export function del(input: Parameters<typeof amplifyDel>[0]): ReturnType<typeof amplifyDel> {
  const inputWithTenant = injectTenantHeader(input)
  return wrapApiCall(() => amplifyDel(inputWithTenant))
}
